import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  Plus, Pencil, Loader2, Search, X, ChevronDown, ChevronRight,
  CheckCircle2, XCircle, AlertCircle, Power, Trash2, History, ArrowRight,
  SlidersHorizontal,
} from 'lucide-react';
import {
  upsertDomain,
  deleteDomain,
  getDomainHistory,
  type WhitelistedDomain,
  type SearchDomain,
  type DomainHistoryEvent,
} from '../../services/api';
import { useAdminData } from './AdminDataContext';
import { SortFilterHeader } from './SharedTableComponents';
import { DomainLogo } from '../../components/BrandLogos';
import { AccountWizard } from './AccountWizard';

const FILTER_LABELS: Record<string, string> = {
  skills: 'Skills',
  exclude: 'Exclude Keywords',
  location: 'Location',
  experience: 'Experience',
  companies: 'Companies',
  education: 'Education',
  school: 'School',
  certificates: 'Certificates',
  researchPapers: 'Research Papers',
  naturalLanguages: 'Natural Languages',
  programmingLanguages: 'Programming Languages',
  rate: 'Rate',
};

const ALL_FILTER_KEYS = Object.keys(FILTER_LABELS);

const KNOWN_DOMAINS = ['SWE', 'STEM', 'Finance', 'DSML'];

interface DomainConfig {
  enabled: boolean;
  appConfig?: {
    showExperience: boolean;
    expertiseOptions: string[];
    fieldOptions: string[];
    filters: Record<string, boolean>;
  };
}

function emptyDomainConfig(): DomainConfig {
  return {
    enabled: false,
    appConfig: {
      showExperience: false,
      expertiseOptions: [],
      fieldOptions: [],
      filters: Object.fromEntries(ALL_FILTER_KEYS.map((k) => [k, false])),
    },
  };
}

// ─── Toggle ────────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, size = 'sm' }: { checked: boolean; onChange: (v: boolean) => void; size?: 'sm' | 'md' }) {
  const h = size === 'md' ? 'h-6 w-11' : 'h-5 w-9';
  const dot = size === 'md' ? 'h-5 w-5' : 'h-4 w-4';
  const translate = size === 'md' ? 'translate-x-5' : 'translate-x-4';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      className={`relative inline-flex ${h} shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${checked ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-700'}`}
    >
      <span className={`pointer-events-none inline-block ${dot} transform rounded-full bg-white shadow ring-0 transition-transform ${checked ? translate : 'translate-x-0'}`} />
    </button>
  );
}

// ─── Portal Overlay ────────────────────────────────────────────────────────
function ModalPortal({ children }: { children: React.ReactNode }) {
  return ReactDOM.createPortal(children, document.body);
}

// ─── Tag Input (inline chips — values may contain commas) ─────────────────
function TagInput({ tags, onChange, placeholder }: { tags: string[]; onChange: (t: string[]) => void; placeholder?: string }) {
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const commit = () => {
    const value = draft.trim();
    if (!value) return;
    if (!tags.includes(value)) onChange([...tags, value]);
    setDraft('');
  };

  const remove = (tag: string) => {
    onChange(tags.filter((t) => t !== tag));
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit();
    } else if (e.key === 'Escape') {
      setDraft('');
    } else if (e.key === 'Backspace' && draft === '' && tags.length > 0) {
      e.preventDefault();
      onChange(tags.slice(0, -1));
    }
  };

  return (
    <div
      onClick={() => inputRef.current?.focus()}
      className="flex flex-wrap items-center gap-1.5 p-2 min-h-[38px] border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 cursor-text hover:border-gray-300 dark:hover:border-gray-600 focus-within:border-blue-300 dark:focus-within:border-blue-600 focus-within:ring-2 focus-within:ring-blue-500 transition-colors"
    >
      {tags.map((tag) => (
        <span
          key={tag}
          className="inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium"
        >
          <span className="truncate">{tag}</span>
          <button
            type="button"
            aria-label={`Remove ${tag}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => { e.stopPropagation(); remove(tag); }}
            className="shrink-0 rounded text-blue-400 hover:text-red-500 transition-colors"
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        onBlur={commit}
        placeholder={tags.length === 0 ? (placeholder ?? 'Type and press Enter to add...') : undefined}
        className="flex-1 min-w-[8rem] border-0 bg-transparent p-0.5 text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none"
      />
    </div>
  );
}

// Filters that have an associated options list
const FILTERS_WITH_OPTIONS: { filterKey: string; optionsKey: 'fieldOptions'; label: string; hint: string }[] = [
  { filterKey: 'field', optionsKey: 'fieldOptions', label: 'Field / Specialization', hint: 'Single-select chips in the search sidebar (e.g., Physics, Chemistry for STEM)' },
];

const FILTERS_WITH_OPTIONS_KEYS = new Set(FILTERS_WITH_OPTIONS.map((f) => f.filterKey));

const SIMPLE_FILTER_KEYS = ALL_FILTER_KEYS.filter((k) => !FILTERS_WITH_OPTIONS_KEYS.has(k));

// ─── Filter With Options Row ───────────────────────────────────────────────
function FilterWithOptionsRow({
  label,
  hint,
  enabled,
  onToggle,
  tags,
  onTagsChange,
}: {
  label: string;
  hint: string;
  enabled: boolean;
  onToggle: (v: boolean) => void;
  tags: string[];
  onTagsChange: (t: string[]) => void;
}) {
  return (
    <div className={`rounded-lg border p-3 transition-colors ${enabled ? 'border-blue-200 dark:border-blue-700 bg-blue-50/40 dark:bg-blue-900/10' : 'border-gray-200 dark:border-gray-800 bg-gray-50/30 dark:bg-gray-900/30'}`}>
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{label}</span>
          <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5 leading-tight">{hint}</p>
        </div>
        <Toggle checked={enabled} onChange={onToggle} />
      </div>
      {enabled && (
        <div className="mt-2">
          <TagInput tags={tags} onChange={onTagsChange} placeholder="Type and press Enter to add..." />
        </div>
      )}
    </div>
  );
}

// ─── Domain Config Card ────────────────────────────────────────────────────
function DomainConfigCard({
  domainKey,
  config,
  onChange,
  onRemove,
}: {
  domainKey: string;
  config: DomainConfig;
  onChange: (c: DomainConfig) => void;
  onRemove: () => void;
}) {
  const [expanded, setExpanded] = useState(config.enabled);
  const appCfg = config.appConfig || emptyDomainConfig().appConfig!;

  const updateAppConfig = (patch: Partial<typeof appCfg>) => {
    onChange({ ...config, appConfig: { ...appCfg, ...patch } });
  };

  const updateFilter = (key: string, val: boolean) => {
    updateAppConfig({ filters: { ...appCfg.filters, [key]: val } });
  };

  const isKnown = KNOWN_DOMAINS.includes(domainKey);

  return (
    <div className={`border rounded-lg transition-colors ${config.enabled ? 'border-blue-200 dark:border-blue-800 bg-blue-50/30 dark:bg-blue-900/10' : 'border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50'}`}>
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer select-none"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-3">
          {expanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{domainKey}</span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${config.enabled ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500'}`}>
            {config.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
        <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
          <Toggle checked={config.enabled} onChange={(v) => onChange({ ...config, enabled: v })} />
          {!isKnown && (
            <button type="button" onClick={onRemove} className="p-1 text-gray-400 hover:text-red-500 rounded transition-colors">
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-200 dark:border-gray-800 pt-4">
          {/* Field / Specialization with options */}
          {FILTERS_WITH_OPTIONS.map(({ filterKey, optionsKey, label, hint }) => (
            <FilterWithOptionsRow
              key={filterKey}
              label={label}
              hint={hint}
              enabled={!!appCfg.filters[filterKey]}
              onToggle={(v) => updateFilter(filterKey, v)}
              tags={appCfg[optionsKey]}
              onTagsChange={(t) => updateAppConfig({ [optionsKey]: t })}
            />
          ))}

          {/* Search filters as pill toggles */}
          <div>
            <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Search Filters</span>
            <div className="flex flex-wrap gap-1.5">
              {SIMPLE_FILTER_KEYS.map((key) => {
                const active = !!appCfg.filters[key];
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => updateFilter(key, !active)}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                      active
                        ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
                        : 'bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 rounded-full ${active ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
                    {FILTER_LABELS[key]}
                  </button>
                );
              })}
              <button
                key="showExperience"
                type="button"
                onClick={() => updateAppConfig({ showExperience: !appCfg.showExperience })}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                  appCfg.showExperience
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800'
                    : 'bg-white dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${appCfg.showExperience ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
                Experience on Card
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Domain Picker ─────────────────────────────────────────────────────────
function DomainPicker({
  domainConfigs,
  availableDomains,
  onAdd,
}: {
  domainConfigs: Record<string, DomainConfig>;
  availableDomains: SearchDomain[];
  onAdd: (key: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current && !containerRef.current.contains(e.target as Node) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openWithPosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownStyle({ top: rect.bottom + 4, left: rect.left, width: rect.width });
    }
    setOpen(true);
  };

  const alreadyAdded = new Set(Object.keys(domainConfigs).map((k) => k.toLowerCase()));
  const filtered = availableDomains.filter(
    (d) =>
      !alreadyAdded.has(d.key.toLowerCase()) &&
      (!query ||
        d.key.toLowerCase().includes(query.toLowerCase()) ||
        d.label.toLowerCase().includes(query.toLowerCase())),
  );
  const noMatch = query.trim().length > 0 && filtered.length === 0;

  const handleSelect = (key: string) => {
    setOpen(false);
    setQuery('');
    onAdd(key);
  };

  return (
    <div ref={containerRef} className="flex-1">
      <input
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); openWithPosition(); }}
        onFocus={openWithPosition}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setOpen(false); setQuery(''); }
          if (e.key === 'Enter' && filtered.length === 1) {
            e.preventDefault();
            handleSelect(filtered[0].key);
          }
        }}
        placeholder="Add domain..."
        className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
      />

      {open && (filtered.length > 0 || noMatch) && ReactDOM.createPortal(
        <div
          ref={dropdownRef}
          style={{ ...dropdownStyle, position: 'fixed', zIndex: 9999 }}
          className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl overflow-hidden"
        >
          {filtered.length > 0 && (
            <div className="overflow-y-auto" style={{ maxHeight: '176px' }}>
              {filtered.map((d) => (
                <button
                  key={d.key}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleSelect(d.key)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{d.key.toUpperCase()}</span>
                  <span className="text-xs text-gray-400">{d.label}</span>
                </button>
              ))}
            </div>
          )}
          {noMatch && (
            <div className="px-3 py-2.5 text-sm text-gray-500 dark:text-gray-400">
              No match found.{' '}
              <span className="text-gray-400 dark:text-gray-500">
                Add new domains in the Managed Domains tab.
              </span>
            </div>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

// ─── Advanced Config Modal ─────────────────────────────────────────────────
// Escape hatch for the per-domain knobs the wizard deliberately does not own
// (filters, expertiseOptions, fieldOptions). Reached only via the explicit
// "Advanced config" action, so nothing here is ever written by default.
function TenantEditModal({
  tenant,
  existingDomains,
  onClose,
  onSaved,
}: {
  tenant: WhitelistedDomain | null;
  existingDomains: string[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { searchDomains: contextSearchDomains } = useAdminData();
  const isNew = !tenant;
  const [domain, setDomain] = useState(tenant?.domain ?? '');
  const [name, setName] = useState(tenant?.name ?? '');
  const [includeGolden, setIncludeGolden] = useState(tenant?.includeGoldenDataset ?? false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [availableDomains, setAvailableDomains] = useState<SearchDomain[]>(contextSearchDomains);

  const [domainConfigs, setDomainConfigs] = useState<Record<string, DomainConfig>>(() => {
    if (tenant?.config && Object.keys(tenant.config).length > 0) {
      const cfg: Record<string, DomainConfig> = {};
      for (const [key, val] of Object.entries(tenant.config)) {
        const v = val as any;
        cfg[key] = {
          enabled: v.enabled ?? false,
          appConfig: v.appConfig ? {
            showExperience: v.appConfig.showExperience ?? false,
            expertiseOptions: v.appConfig.expertiseOptions ?? [],
            fieldOptions: v.appConfig.fieldOptions ?? [],
            filters: { ...v.appConfig.filters },
          } : emptyDomainConfig().appConfig!,
        };
      }
      return cfg;
    }
    return {};
  });

  const hasConfig = Object.keys(domainConfigs).length > 0;

  useEffect(() => { setAvailableDomains(contextSearchDomains); }, [contextSearchDomains]);

  const addDomainEntry = (key: string) => {
    const k = key.trim().toUpperCase();
    if (!k || domainConfigs[k]) return;
    setDomainConfigs((prev) => ({ ...prev, [k]: emptyDomainConfig() }));
  };

  const removeDomainEntry = (key: string) => {
    setDomainConfigs((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const handleAddDomain = (key: string) => {
    addDomainEntry(key);
  };

  const handleSave = async () => {
    if (!domain.trim()) { setError('Domain is required'); return; }
    if (isNew && existingDomains.includes(domain.trim().toLowerCase())) {
      setError(`An account with domain "${domain.trim().toLowerCase()}" already exists.`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      // Merge onto each domain's original config rather than replacing it outright —
      // this editor only models a subset of appConfig, so a plain overwrite would
      // silently drop fields it doesn't know about (e.g. skillsConfig set via the
      // Skills page, or domainFilterOptions), since the backend replaces `config` wholesale.
      const originalConfig = (tenant?.config ?? {}) as Record<string, any>;
      const config = hasConfig
        ? Object.fromEntries(Object.entries(domainConfigs).map(([key, cfg]) => {
            const originalDomainCfg = originalConfig[key] ?? {};
            const originalAppConfig = originalDomainCfg.appConfig ?? {};
            return [key, {
              ...originalDomainCfg,
              enabled: cfg.enabled,
              appConfig: cfg.appConfig ? {
                ...originalAppConfig,
                showExperience: cfg.appConfig.showExperience,
                expertiseOptions: cfg.appConfig.expertiseOptions,
                fieldOptions: cfg.appConfig.fieldOptions,
                filters: cfg.appConfig.filters,
              } : originalAppConfig,
            }];
          }))
        : undefined;
      await upsertDomain({ domain: domain.trim().toLowerCase(), name: name.trim() || undefined, config, includeGoldenDataset: includeGolden });
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 overflow-y-auto" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-3xl mx-4 my-12 animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {isNew ? 'Add Account' : 'Edit Account'}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {isNew ? 'Whitelist a new email domain and configure search access.' : `Editing ${tenant.domain}`}
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-2">
              <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
            </div>
          )}

          {/* Basic Info */}
          <div className="space-y-4">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Basic Info</h4>
            <div className="grid grid-cols-2 gap-4">
              <label className="block">
                <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Email Domain</span>
                <input
                  type="text"
                  value={domain}
                  onChange={(e) => setDomain(e.target.value)}
                  disabled={!isNew}
                  placeholder="e.g. acme.com"
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Display Name</span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={!isNew}
                  placeholder="e.g. Acme Corp"
                  title={isNew ? undefined : 'Rename from Edit — admin access is keyed on this name and must be migrated with it.'}
                  className="w-full px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
                />
              </label>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-gray-200 dark:border-gray-800 px-3 py-2.5">
              <div>
                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">Golden Dataset</span>
                <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">Include golden-flagged talents in this account's search (sync).</span>
              </div>
              <Toggle checked={includeGolden} onChange={setIncludeGolden} />
            </div>
          </div>

          {/* Domain Config */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Managed Domain Configuration</h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {hasConfig ? 'Custom config for this tenant.' : 'No custom config — system defaults will be used.'}
                </p>
              </div>
            </div>

            {!hasConfig && (
              <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-700 p-6 text-center">
                <AlertCircle className="w-6 h-6 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">This account uses system default configuration.</p>
                <button
                  type="button"
                  onClick={() => {
                    const first = availableDomains.find((d) => d.isActive);
                    const key = first ? first.key.toUpperCase() : 'SWE';
                    setDomainConfigs({ [key]: emptyDomainConfig() });
                  }}
                  className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-400 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                >
                  Customize Config
                </button>
              </div>
            )}

            {hasConfig && (
              <div className="space-y-3">
                {Object.entries(domainConfigs).map(([key, cfg]) => (
                  <DomainConfigCard
                    key={key}
                    domainKey={key}
                    config={cfg}
                    onChange={(c) => setDomainConfigs((prev) => ({ ...prev, [key]: c }))}
                    onRemove={() => removeDomainEntry(key)}
                  />
                ))}

                <div className="flex items-center gap-2 pt-1">
                  <DomainPicker
                    domainConfigs={domainConfigs}
                    availableDomains={availableDomains}
                    onAdd={handleAddDomain}
                  />
                </div>

                <button
                  type="button"
                  onClick={() => setDomainConfigs({})}
                  className="text-xs text-gray-400 hover:text-red-500 transition-colors"
                >
                  Clear custom config (revert to system defaults)
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 rounded-b-xl flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !domain.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? 'Saving...' : isNew ? 'Create Account' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Confirm Dialog ────────────────────────────────────────────────────────
function ConfirmDialog({
  title,
  message,
  confirmLabel,
  destructive,
  onConfirm,
  onCancel,
}: {
  title: string;
  message: string;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onCancel}>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-sm animate-in zoom-in-95 duration-200" onClick={(e) => e.stopPropagation()}>
        <div className="p-6 space-y-3">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{message}</p>
        </div>
        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 rounded-b-xl flex justify-end gap-3">
          <button onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${destructive ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    + ', '
    + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/** Turns a flatDiff dot-path key (e.g. "config.SWE.appConfig.filters.rate") into a short human label. */
function humanizeChangeKey(key: string): string {
  if (key === 'name') return 'Account Name';
  if (key === 'isActive') return 'Status';

  const parts = key.split('.');
  if (parts[0] !== 'config' || !parts[1]) return key;
  const domain = parts[1];
  const rest = parts.slice(2);

  if (rest.length === 0) return `${domain} configuration`;
  if (rest[0] === 'enabled') return `${domain} enabled`;
  if (rest[0] === 'appConfig') {
    const sub = rest[1];
    if (sub === 'showExperience') return `${domain}: show experience`;
    if (sub === 'expertiseOptions') return `${domain}: Expertise options`;
    if (sub === 'fieldOptions') return `${domain}: Field options`;
    if (sub === 'domainFilterOptions') return `${domain}: Domain filter options`;
    if (sub === 'skillsConfig') return `${domain}: Skill suggestions`;
    if (sub === 'filters' && rest[2]) {
      const filterLabel = FILTER_LABELS[rest[2]] ?? rest[2];
      return `${domain} filter: ${filterLabel}`;
    }
    return `${domain} settings`;
  }
  return key;
}

/** Formats a raw value for display inside a before/after pill. */
function formatPillValue(v: unknown): string {
  if (v === null || v === undefined || v === '') return 'Not set';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (Array.isArray(v)) {
    if (v.length === 0) return 'Not set';
    if (v.length <= 3) return v.join(', ');
    return `${v.slice(0, 3).join(', ')} +${v.length - 3} more`;
  }
  if (typeof v === 'object') return 'Custom configuration';
  return String(v);
}

function ChangePill({ tone, value }: { tone: 'from' | 'to'; value: unknown }) {
  const cls = tone === 'from'
    ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800'
    : 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium ${cls}`}>
      {formatPillValue(value)}
    </span>
  );
}

/** Renders a bare scalar/leaf value (no surrounding block punctuation). */
function JsonLeaf({ value }: { value: unknown }) {
  if (value === null || value === undefined) {
    return <span className="text-gray-400 dark:text-gray-600">null</span>;
  }
  if (typeof value === 'string') {
    return <span className="text-emerald-700 dark:text-emerald-400">&quot;{value}&quot;</span>;
  }
  if (typeof value === 'boolean' || typeof value === 'number') {
    return <span className="text-amber-700 dark:text-amber-400">{String(value)}</span>;
  }
  return <span>{String(value)}</span>;
}

/**
 * Renders one JSON value (real curly/square brackets, trailing commas glued to their
 * closing line) with its own expand/collapse chevron when it's a non-empty object or
 * array — mimicking a browser devtools JSON tree. Collapsed shows a `{ … 3 keys }`
 * summary in place of the children.
 */
function JsonNode({ keyPrefix, value, indent, comma }: {
  keyPrefix?: React.ReactNode;
  value: unknown;
  indent: number;
  comma: boolean;
}) {
  const [open, setOpen] = useState(true);
  const pad = { paddingLeft: `${indent * 8}px` };
  const suffix = comma ? ',' : '';

  const isArray = Array.isArray(value);
  const isObject = !isArray && value !== null && typeof value === 'object';
  const entries = isObject ? Object.entries(value as Record<string, unknown>) : [];
  const items = isArray ? (value as unknown[]) : [];
  const isContainer = (isArray && items.length > 0) || (isObject && entries.length > 0);

  if (!isContainer) {
    if (isArray) return <div style={pad}>{keyPrefix}<span className="text-gray-400 dark:text-gray-600">[]</span>{suffix}</div>;
    if (isObject) return <div style={pad}>{keyPrefix}<span className="text-gray-400 dark:text-gray-600">{'{}'}</span>{suffix}</div>;
    return <div style={pad}>{keyPrefix}<JsonLeaf value={value} />{suffix}</div>;
  }

  const openBracket = isArray ? '[' : '{';
  const closeBracket = isArray ? ']' : '}';
  const count = isArray ? items.length : entries.length;

  return (
    <div>
      <div style={pad} className="flex items-center gap-0.5">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          aria-label={open ? 'Collapse' : 'Expand'}
        >
          {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        </button>
        <span>
          {keyPrefix}
          {openBracket}
          {!open && (
            <>
              <span className="text-gray-400 dark:text-gray-600"> … {count} {isArray ? 'items' : 'keys'} </span>
              {closeBracket}
              {suffix}
            </>
          )}
        </span>
      </div>
      {open && (
        <>
          {isArray
            ? items.map((v, i) => (
                <JsonNode key={i} value={v} indent={indent + 1} comma={i < items.length - 1} />
              ))
            : entries.map(([k, v], i) => (
                <JsonNode
                  key={k}
                  keyPrefix={(
                    <>
                      <span className="text-sky-700 dark:text-sky-400">{k}</span>
                      <span className="text-gray-500 dark:text-gray-400">: </span>
                    </>
                  )}
                  value={v}
                  indent={indent + 1}
                  comma={i < entries.length - 1}
                />
              ))}
          <div style={pad}>{closeBracket}{suffix}</div>
        </>
      )}
    </div>
  );
}

function JsonBlock({ value }: { value: unknown }) {
  return <JsonNode value={value} indent={0} comma={false} />;
}

/** Rebuilds a nested object from flatDiff's dot-path keys, e.g. "config.SWE.enabled" → { config: { SWE: { enabled: ... } } }. */
function unflattenChanges(
  changes: Record<string, { from: unknown; to: unknown }>,
  side: 'from' | 'to',
): Record<string, unknown> {
  const root: Record<string, unknown> = {};
  for (const [path, pair] of Object.entries(changes)) {
    const parts = path.split('.');
    let node = root;
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      const existing = node[part];
      if (typeof existing !== 'object' || existing === null || Array.isArray(existing)) {
        node[part] = {};
      }
      node = node[part] as Record<string, unknown>;
    }
    node[parts[parts.length - 1]] = pair[side];
  }
  return root;
}

function RawChangesColumn({ label, changes, side }: {
  label: string;
  changes: Record<string, { from: unknown; to: unknown }>;
  side: 'from' | 'to';
}) {
  const tree = unflattenChanges(changes, side);
  return (
    <div className="rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800/70 border-b border-gray-200 dark:border-gray-700">
        {label}
      </div>
      <div className="p-2 max-h-64 overflow-auto text-[11px] font-mono text-gray-700 dark:text-gray-300">
        <JsonBlock value={tree} />
      </div>
    </div>
  );
}

function RawChangesToggle({ changes }: { changes: Record<string, { from: unknown; to: unknown }> }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline"
      >
        {open ? 'Hide raw details' : 'View raw details'}
      </button>
      {open && (
        <div className="mt-2 grid grid-cols-2 gap-2">
          <RawChangesColumn label="Before" changes={changes} side="from" />
          <RawChangesColumn label="After" changes={changes} side="to" />
        </div>
      )}
    </div>
  );
}

const HISTORY_ICON: Record<string, { icon: React.ElementType; bg: string; text: string; border: string }> = {
  created: { icon: Plus, bg: 'bg-green-50 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400', border: 'border-green-100 dark:border-green-800' },
  updated: { icon: Pencil, bg: 'bg-blue-50 dark:bg-blue-900/30', text: 'text-blue-600 dark:text-blue-400', border: 'border-blue-100 dark:border-blue-800' },
  reactivated: { icon: Power, bg: 'bg-green-50 dark:bg-green-900/30', text: 'text-green-600 dark:text-green-400', border: 'border-green-100 dark:border-green-800' },
  deactivated: { icon: Power, bg: 'bg-red-50 dark:bg-red-900/30', text: 'text-red-600 dark:text-red-400', border: 'border-red-100 dark:border-red-800' },
};

// ─── Account History Modal ─────────────────────────────────────────────────
function AccountHistoryModal({ tenant, onClose }: { tenant: WhitelistedDomain; onClose: () => void }) {
  const [history, setHistory] = useState<DomainHistoryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getDomainHistory(tenant.domain);
        if (!cancelled) setHistory(data);
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.message || err.message || 'Failed to load history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenant.domain]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-2xl mx-4 my-12 animate-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Account History</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{tenant.domain}</p>
          </div>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : error ? (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-2">
              <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
              <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-12 text-sm text-gray-400 dark:text-gray-500">No activity logged yet.</div>
          ) : (
            <div className="relative">
              <div className="absolute left-4 top-2 bottom-2 w-px bg-gray-200 dark:bg-gray-700" />
              <div className="space-y-5">
                {history.map((event) => {
                  const config = HISTORY_ICON[event.action] ?? HISTORY_ICON.updated;
                  const Icon = config.icon;
                  const changes = (event.details?.changes ?? null) as Record<string, { from: unknown; to: unknown }> | null;
                  return (
                    <div key={event.id} className="relative flex items-start gap-4">
                      <div className={`z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${config.bg} ${config.text} ${config.border}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="min-w-0 flex-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3">
                        <div className="flex flex-wrap items-baseline gap-x-1.5 gap-y-1 text-sm">
                          <span className="font-semibold text-gray-900 dark:text-gray-100">{event.actorEmail}</span>
                          <span className="text-gray-500 dark:text-gray-400">{event.action}</span>
                          <span className="text-gray-500 dark:text-gray-400">this account</span>
                        </div>
                        <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">{formatDateTime(event.createdAt)}</div>
                        {changes && Object.keys(changes).length > 0 && (
                          <div className="mt-2 space-y-2 border-t border-gray-100 dark:border-gray-800 pt-2">
                            {Object.entries(changes)
                              // Older log rows can contain entries where both sides render
                              // identically (e.g. both "Not set") — hide those rather than
                              // showing a no-op change, even though new rows won't have them.
                              .filter(([, { from, to }]) => formatPillValue(from) !== formatPillValue(to))
                              .map(([key, { from, to }]) => (
                                <div key={key} className="flex flex-wrap items-center gap-2 text-xs">
                                  <span className="text-gray-500 dark:text-gray-400">{humanizeChangeKey(key)}</span>
                                  <ChangePill tone="from" value={from} />
                                  <ArrowRight className="w-3 h-3 text-gray-400 flex-shrink-0" />
                                  <ChangePill tone="to" value={to} />
                                </div>
                              ))}
                            <RawChangesToggle changes={changes} />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 rounded-b-xl flex justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

interface AccountFilters {
  domain?: string[];
  name?: string[];
  status?: string[];
  managedDomains?: string[];
  addedBy?: string[];
  updatedBy?: string[];
}

// ─── Main Section ──────────────────────────────────────────────────────────
export function TenantsSection() {
  const {
    tenants: contextTenants,
    searchDomains: contextSearchDomains,
    refreshTenants: contextRefreshTenants,
  } = useAdminData();
  const [tenants, setTenants] = useState<WhitelistedDomain[]>(contextTenants);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [columnFilters, setColumnFilters] = useState<AccountFilters>({ status: ['Active'] });
  const [sort, setSort] = useState<{ col: string; dir: 'asc' | 'desc' } | null>(null);
  const [wizardTarget, setWizardTarget] = useState<WhitelistedDomain | null | 'new'>(null);
  const [advancedTarget, setAdvancedTarget] = useState<WhitelistedDomain | null>(null);
  const [deactivateTarget, setDeactivateTarget] = useState<WhitelistedDomain | null>(null);
  const [historyTarget, setHistoryTarget] = useState<WhitelistedDomain | null>(null);
  const [expandedDomains, setExpandedDomains] = useState<Set<number>>(new Set());
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => { setTenants(contextTenants); }, [contextTenants]);

  const fetchTenants = useCallback(async () => {
    try {
      await contextRefreshTenants();
      setError('');
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  }, [contextRefreshTenants]);

  useEffect(() => { if (contextTenants.length > 0) setLoading(false); }, [contextTenants]);

  const getEnabledDomains = (t: WhitelistedDomain): string[] => {
    if (!t.config || typeof t.config !== 'object') return [];
    return Object.entries(t.config)
      .filter(([, v]) => (v as any)?.enabled)
      .map(([k]) => k);
  };

  // Status cards and the Status column filter are the same filter.
  const statusFilter = columnFilters.status?.length === 1
    ? (columnFilters.status[0] === 'Active' ? 'active' : 'inactive')
    : 'all';
  const setStatusFilter = (v: 'all' | 'active' | 'inactive') =>
    setColumnFilters((prev) => ({ ...prev, status: v === 'all' ? undefined : [v === 'active' ? 'Active' : 'Inactive'] }));

  const filtered = tenants.filter((t) => {
    if (columnFilters.domain?.length && !columnFilters.domain.includes(t.domain)) return false;
    if (columnFilters.name?.length && !(t.name && columnFilters.name.includes(t.name))) return false;
    if (columnFilters.status?.length && !columnFilters.status.includes(t.isActive ? 'Active' : 'Inactive')) return false;
    if (columnFilters.managedDomains?.length) {
      const enabled = getEnabledDomains(t);
      if (!columnFilters.managedDomains.some((d) => enabled.includes(d))) return false;
    }
    if (columnFilters.addedBy?.length && !(t.addedBy && columnFilters.addedBy.includes(t.addedBy))) return false;
    if (columnFilters.updatedBy?.length && !(t.updatedBy && columnFilters.updatedBy.includes(t.updatedBy))) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return t.domain.toLowerCase().includes(q) || (t.name?.toLowerCase().includes(q));
  });

  const filterOpts = React.useMemo(() => {
    const domains = new Set<string>();
    const names = new Set<string>();
    const managedDomains = new Set<string>();
    const addedBys = new Set<string>();
    const updatedBys = new Set<string>();
    for (const t of tenants) {
      if (t.domain) domains.add(t.domain);
      if (t.name) names.add(t.name);
      for (const d of getEnabledDomains(t)) managedDomains.add(d);
      if (t.addedBy) addedBys.add(t.addedBy);
      if (t.updatedBy) updatedBys.add(t.updatedBy);
    }
    return {
      domains: Array.from(domains).sort(),
      names: Array.from(names).sort(),
      managedDomains: Array.from(managedDomains).sort(),
      addedBys: Array.from(addedBys).sort(),
      updatedBys: Array.from(updatedBys).sort(),
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenants]);

  const sorted = React.useMemo(() => {
    if (!sort) return filtered;
    const dir = sort.dir === 'asc' ? 1 : -1;
    const sortValue = (t: WhitelistedDomain): string | number => {
      switch (sort.col) {
        case 'domain': return t.domain.toLowerCase();
        case 'name': return (t.name || '').toLowerCase();
        case 'status': return t.isActive ? 0 : 1;
        case 'managedDomains': return [...getEnabledDomains(t)].sort().join(',');
        case 'addedBy': return (t.addedBy || '').toLowerCase();
        case 'createdAt': return new Date(t.createdAt).getTime();
        case 'updatedBy': return (t.updatedBy || '').toLowerCase();
        case 'updatedAt': return new Date(t.updatedAt).getTime();
        default: return '';
      }
    };
    return [...filtered].sort((a, b) => {
      const va = sortValue(a);
      const vb = sortValue(b);
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return 0;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, sort]);

  const handleSort = (col: string) => {
    setSort((prev) => (prev?.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' }));
  };

  const handleColumnFilter = (key: keyof AccountFilters, value: string[]) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value.length > 0 ? value : undefined }));
  };

  const hasActiveFilters = search.length > 0 || Object.values(columnFilters).some((v) => v && v.length > 0);

  const activeTenants = tenants.filter((t) => t.isActive);

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setActionLoading(true);
    try {
      await deleteDomain(deactivateTarget.domain);
      setDeactivateTarget(null);
      fetchTenants();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to deactivate');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReactivate = async (t: WhitelistedDomain) => {
    setActionLoading(true);
    try {
      await upsertDomain({ domain: t.domain, name: t.name || undefined });
      fetchTenants();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to reactivate');
    } finally {
      setActionLoading(false);
    }
  };

  if (wizardTarget) {
    return (
      <AccountWizard
        tenant={wizardTarget === 'new' ? null : wizardTarget}
        tenants={tenants}
        searchDomains={contextSearchDomains}
        onCancel={() => setWizardTarget(null)}
        onComplete={async () => {
          await fetchTenants();
          setWizardTarget(null);
        }}
      />
    );
  }

  return (
    <div className="p-8 max-w-[1400px] mx-auto space-y-6 transition-opacity duration-300">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Accounts</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Manage whitelisted domains and their search configuration.</p>
        </div>
        <button
          onClick={() => setWizardTarget('new')}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
        >
          <Plus className="w-4 h-4" />
          Add Account
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <button
          onClick={() => setStatusFilter(statusFilter === 'all' ? 'all' : 'all')}
          className={`text-left bg-white dark:bg-gray-900 rounded-xl border p-4 transition-colors cursor-pointer ${statusFilter === 'all' ? 'border-blue-500 ring-2 ring-blue-500/20' : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'}`}
        >
          <div className="text-2xl font-semibold text-gray-900 dark:text-gray-100">{tenants.length}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Total Accounts</div>
        </button>
        <button
          onClick={() => setStatusFilter(statusFilter === 'active' ? 'all' : 'active')}
          className={`text-left bg-white dark:bg-gray-900 rounded-xl border p-4 transition-colors cursor-pointer ${statusFilter === 'active' ? 'border-green-500 ring-2 ring-green-500/20' : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'}`}
        >
          <div className="text-2xl font-semibold text-green-600">{activeTenants.length}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Active</div>
        </button>
        <button
          onClick={() => setStatusFilter(statusFilter === 'inactive' ? 'all' : 'inactive')}
          className={`text-left bg-white dark:bg-gray-900 rounded-xl border p-4 transition-colors cursor-pointer ${statusFilter === 'inactive' ? 'border-gray-500 ring-2 ring-gray-500/20' : 'border-gray-200 dark:border-gray-800 hover:border-gray-300 dark:hover:border-gray-700'}`}
        >
          <div className="text-2xl font-semibold text-gray-400">{tenants.length - activeTenants.length}</div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Inactive</div>
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-2">
          <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by domain or name..."
          className="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {search && (
          <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1200px]">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                <SortFilterHeader label="Email Domain" sortKey="domain" currentSort={sort} onSort={handleSort} filterOptions={filterOpts.domains} filterValue={columnFilters.domain} onFilter={(v) => handleColumnFilter('domain', v)} />
                <SortFilterHeader label="Account" sortKey="name" currentSort={sort} onSort={handleSort} filterOptions={filterOpts.names} filterValue={columnFilters.name} onFilter={(v) => handleColumnFilter('name', v)} />
                <SortFilterHeader label="Status" sortKey="status" currentSort={sort} onSort={handleSort} filterOptions={['Active', 'Inactive']} filterValue={columnFilters.status} onFilter={(v) => handleColumnFilter('status', v)} />
                <SortFilterHeader label="Managed Domains" sortKey="managedDomains" currentSort={sort} onSort={handleSort} filterOptions={filterOpts.managedDomains} filterValue={columnFilters.managedDomains} onFilter={(v) => handleColumnFilter('managedDomains', v)} />
                <SortFilterHeader label="Added By" sortKey="addedBy" currentSort={sort} onSort={handleSort} filterOptions={filterOpts.addedBys} filterValue={columnFilters.addedBy} onFilter={(v) => handleColumnFilter('addedBy', v)} />
                <SortFilterHeader label="Added At" sortKey="createdAt" currentSort={sort} onSort={handleSort} />
                <SortFilterHeader label="Updated By" sortKey="updatedBy" currentSort={sort} onSort={handleSort} filterOptions={filterOpts.updatedBys} filterValue={columnFilters.updatedBy} onFilter={(v) => handleColumnFilter('updatedBy', v)} />
                <SortFilterHeader label="Updated" sortKey="updatedAt" currentSort={sort} onSort={handleSort} />
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-4 py-16 text-center text-sm text-gray-500 dark:text-gray-400">
                    {hasActiveFilters ? (
                      <>
                        No accounts match your search/filters.{' '}
                        <button
                          onClick={() => { setSearch(''); setStatusFilter('all'); setColumnFilters({}); }}
                          className="text-blue-600 dark:text-blue-400 underline hover:text-blue-800"
                        >
                          Clear filters
                        </button>
                      </>
                    ) : 'No accounts configured yet.'}
                  </td>
                </tr>
              )}
              {sorted.map((t) => {
                const enabled = getEnabledDomains(t);
                return (
                  <tr key={t.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                      <button
                        onClick={() => setHistoryTarget(t)}
                        className="hover:text-blue-600 dark:hover:text-blue-400 hover:underline transition-colors"
                        title="View History"
                      >
                        {t.domain}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                      <div className="flex items-center gap-2">
                        <DomainLogo domain={t.domain} size="sm" />
                        <span>{t.name || '—'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                        t.isActive
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                          : 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-500'
                      }`}>
                        {t.isActive ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                        {t.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-[240px]">
                      <div className="flex flex-wrap gap-1">
                        {enabled.length > 0 ? (
                          <>
                            {(expandedDomains.has(t.id) ? enabled : enabled.slice(0, 4)).map((d) => (
                              <span key={d} className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 whitespace-nowrap">
                                {d}
                              </span>
                            ))}
                            {enabled.length > 4 && (
                              <button
                                onClick={() => setExpandedDomains((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(t.id)) next.delete(t.id); else next.add(t.id);
                                  return next;
                                })}
                                className="px-1.5 py-0.5 rounded text-[10px] font-medium text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
                              >
                                {expandedDomains.has(t.id) ? 'Show less' : `+${enabled.length - 4} more`}
                              </button>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">
                      {t.addedBy || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                      {new Date(t.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">
                      {t.updatedBy || '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs">
                      {new Date(t.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => setHistoryTarget(t)}
                          className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30 rounded-lg transition-colors"
                          title="View History"
                        >
                          <History className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setWizardTarget(t)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setAdvancedTarget(t)}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                          title="Advanced config (filters, expertise & field options)"
                        >
                          <SlidersHorizontal className="w-4 h-4" />
                        </button>
                        {t.isActive ? (
                          <button
                            onClick={() => setDeactivateTarget(t)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                            title="Deactivate"
                          >
                            <Power className="w-4 h-4" />
                          </button>
                        ) : (
                          <button
                            onClick={() => handleReactivate(t)}
                            disabled={actionLoading}
                            className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors disabled:opacity-50"
                            title="Reactivate"
                          >
                            <Power className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* Modals — portaled to body to escape any parent stacking context */}
      {advancedTarget && (
        <ModalPortal>
          <TenantEditModal
            tenant={advancedTarget}
            existingDomains={tenants.map((t) => t.domain.toLowerCase())}
            onClose={() => setAdvancedTarget(null)}
            onSaved={() => { setAdvancedTarget(null); fetchTenants(); }}
          />
        </ModalPortal>
      )}

      {deactivateTarget && (
        <ModalPortal>
          <ConfirmDialog
            title="Deactivate Account"
            message={`This will deactivate "${deactivateTarget.domain}". Users from this domain will lose access. You can reactivate it later.`}
            confirmLabel="Deactivate"
            destructive
            onConfirm={handleDeactivate}
            onCancel={() => setDeactivateTarget(null)}
          />
        </ModalPortal>
      )}

      {historyTarget && (
        <ModalPortal>
          <AccountHistoryModal tenant={historyTarget} onClose={() => setHistoryTarget(null)} />
        </ModalPortal>
      )}
    </div>
  );
}
